import { useAppForm } from "@/components/form/form-context";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { createOrganisationFn } from "@/server/organisations";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard/organisations/new-org")({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const createOrganisationMutation = useMutation({
    mutationFn: createOrganisationFn,
    onError: (error) => {
      console.error(error);

      //TODO: Actual error handling
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      navigate({ to: `/organisations` });
    },
  });

  const createOrganisation = async (payload) => {
    try {
      const organisationPromise = createOrganisationMutation.mutateAsync({ data: { name: payload.name } });
      toast.promise(organisationPromise, {
        loading: "Creating organisation...",
        success: (data) => {
          return `Organisation created: ${data?.data?.name}`;
        },
        error: (error) => error.message || "Failed to create organisation",
      });
    } catch (error) {
      console.error(error);
    }
  };

  const form = useAppForm({
    defaultValues: {
      name: "",
    },
    onSubmit: async (formData) => {
      createOrganisation(formData.value);
    },
  });

  const getError = (field: any) => {
    const error = form.getAllErrors().fields;
    const errorName = Object.keys(error).find((key) => key === field.name);
    if (errorName) {
      const errorMessage = error[errorName].errors[0][errorName];
      return errorMessage;
    }
    return undefined;
  };

  const navigate = useNavigate();

  return (
    <section className="flex flex-col gap-8">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Create new organisation</CardTitle>
          </CardHeader>
          <CardContent>
            <form.AppField
              name="name"
              validators={{
                onChange: (value) => {
                  if (value.value.length < 1) {
                    return {
                      name: "Name is required",
                    };
                  }
                },
              }}
              children={(field) => (
                <field.FormTextField
                  label="Name"
                  id="name"
                  description="Name of the organisation"
                  error={getError(field)}
                />
              )}
            />
          </CardContent>
        </Card>
        <form.Subscribe
          selector={(state) => [state.isSubmitting, state.isDirty, state.canSubmit]}
          children={([isSubmitting, isDirty, canSubmit]) => (
            <div className="flex gap-2 mt-4">
              <Button type="submit" disabled={isSubmitting || !isDirty || !canSubmit}>
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate({ to: "/organisations" })}>
                Cancel
              </Button>
            </div>
          )}
        />
      </form>
    </section>
  );
}
